import { NavigationItem, UserData } from 'common/models/navigation';
import React, { useState } from 'react';
import { Logo } from '../Logo/Logo';
import { UserProfile } from 'components/organisms/User-Profile/UserProfile';
import { Navigation } from '../NavigationMenu/navigation-menu';
import AnalystSidebarButton from 'components/feature-explainer/analyst-sidebar-button';
import FeatureExplainerSidebarButton from 'components/feature-explainer/feature-explainer-sidebar-button';
import AiHelperSidebarButton from 'components/feature-explainer/ai-helper-sidebar-button';

interface SidebarProps {
	collapsed: boolean;
	onToggle: () => void;
	activeItem?: string;
	onNavigate?: (_item: NavigationItem) => void;
	user: UserData;
	permittedPages?: string[];
	insideDrawer?: boolean;
	onSettings?: () => void;
	onAnalyst?: () => void;
	analystActive?: boolean;
	onAiHelper?: () => void;
	finderActive?: boolean;
	aiHelperBusy?: boolean;
	onFeatureExplainer?: () => void;
	explainerActive?: boolean;
	featureExplainerBusy?: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({
	collapsed,
	onToggle,
	permittedPages,
	user,
	insideDrawer = false,
	onSettings,
	onAnalyst,
	analystActive = false,
	onAiHelper,
	finderActive = false,
	aiHelperBusy = false,
	onFeatureExplainer,
	explainerActive = false,
	featureExplainerBusy = false,
}) => {
	const [isHovered, setIsHovered] = useState(false);

	const isExpanded = !collapsed || (collapsed && isHovered);
	const showAssistant = Boolean(onAnalyst || onAiHelper || onFeatureExplainer);

	const positionClasses = insideDrawer
		? 'relative' // inside drawer -> follow drawer container
		: 'fixed top-0 left-0 z-[60] md:relative md:z-auto'; // desktop/original

	const widthClasses = insideDrawer
		? 'w-full' // drawer defines the width
		: collapsed
		? 'w-[var(--sidebar-collapsed)] min-w-[var(--sidebar-collapsed)] max-w-[var(--sidebar-collapsed)]'
		: 'w-[var(--sidebar-open)] min-w-[var(--sidebar-open)] max-w-[var(--sidebar-open)]';

	return (
		<div
			className={`bg-white z-[60] flex flex-col h-full min-h-0 overflow-hidden transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] border-r-0 md:border-r border-tertiary-background ${positionClasses} ${widthClasses}`}
			onMouseEnter={() => collapsed && setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<Logo
				collapsed={!isExpanded}
				onToggleSidebar={onToggle}
				onNavigateHome={insideDrawer ? onToggle : undefined}
			/>

			<div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col">
				<Navigation collapsed={!isExpanded} permittedPages={permittedPages} onClose={insideDrawer ? onToggle : undefined} />
			</div>

			<div className="flex-shrink-0 flex flex-col gap-1 pt-2 pb-1">
				{showAssistant && (
					<div className="px-3">
						<div
							role="group"
							{...(isExpanded
								? { 'aria-labelledby': 'sidebar-assistant-heading' }
								: { 'aria-label': 'AI Assistant' })}
							className={`rounded-2xl border border-tertiary-background bg-primary-background ${
								isExpanded ? 'px-2 py-2' : 'px-1.5 py-1.5'
							}`}
						>
							{isExpanded && (
								<p
									id="sidebar-assistant-heading"
									className="px-1 pb-1.5 pt-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-secondary-text"
								>
									AI Assistant
								</p>
							)}
							<div
								className={`flex flex-col gap-0.5 ${isExpanded ? 'items-stretch' : 'items-center'}`}
							>
								{onAnalyst && (
									<AnalystSidebarButton
										collapsed={!isExpanded}
										active={analystActive}
										onClick={onAnalyst}
										fullWidth={isExpanded}
									/>
								)}
								{onAiHelper && (
									<AiHelperSidebarButton
										collapsed={!isExpanded}
										active={finderActive}
										busy={aiHelperBusy}
										onClick={onAiHelper}
										fullWidth={isExpanded}
									/>
								)}
								{onFeatureExplainer && (
									<FeatureExplainerSidebarButton
										collapsed={!isExpanded}
										active={explainerActive}
										busy={featureExplainerBusy}
										onClick={onFeatureExplainer}
										fullWidth={isExpanded}
									/>
								)}
							</div>
						</div>
					</div>
				)}
				<div
					className={`flex items-center  ${
						!isExpanded ? 'justify-center' : 'justify-start py-[14px] pl-[18px] pr-3'
					}`}
				>
					<UserProfile user={user} collapsed={!isExpanded} onSettings={onSettings} />
				</div>
			</div>
		</div>
	);
};
